/**
 * Koneko A access / visibility overrides — shared across browsers via API.
 *
 * GET/PUT {base}api/koneko/v1/access/
 *   Body on PUT: { overrides: { victor: { "konekoA.org_budget_editor": true } } }
 *   Optional: { replace: true } to replace the full map (import).
 */
import {getKonekoBudgetApiBaseUrl} from "@/utils/apiBasePreference";

const REQ_MS = 8000;

function authHeaders() {
  const h = {Accept: "application/json"};
  try {
    const token = localStorage.getItem("token");
    if (token) h.Authorization = `JWT ${token}`;
  } catch {
    /* ignore */
  }
  return h;
}

function joinApi(base, path) {
  const b = String(base || "").replace(/\/?$/, "/");
  const p = String(path || "").replace(/^\//, "");
  return `${b}${p}`.replace(/([^:]\/)\/+/g, "$1");
}

async function getJsonOrNull(url) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), REQ_MS);
  try {
    const r = await fetch(url, {
      method: "GET",
      cache: "no-store",
      credentials: "omit",
      mode: "cors",
      headers: authHeaders(),
      signal: ctrl.signal,
    });
    clearTimeout(t);
    if (!r.ok) return null;
    const ct = (r.headers.get("content-type") || "").toLowerCase();
    if (!ct.includes("application/json")) return null;
    return await r.json();
  } catch {
    clearTimeout(t);
    return null;
  }
}

async function putJson(url, bodyObj) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), REQ_MS);
  const headers = {...authHeaders(), "Content-Type": "application/json"};
  try {
    const r = await fetch(url, {
      method: "PUT",
      cache: "no-store",
      credentials: "omit",
      mode: "cors",
      headers,
      body: JSON.stringify(bodyObj || {}),
      signal: ctrl.signal,
    });
    clearTimeout(t);
    let detail = "";
    const ct = (r.headers.get("content-type") || "").toLowerCase();
    const text = await r.text();
    if (ct.includes("application/json")) {
      try {
        const j = JSON.parse(text);
        detail = (j && (j.detail || j.message)) || text;
      } catch {
        detail = text;
      }
    } else {
      detail = text.slice(0, 240);
    }
    return {ok: r.ok, status: r.status, detail};
  } catch (e) {
    clearTimeout(t);
    return {ok: false, status: 0, detail: (e && e.message) || "network"};
  }
}

/** @returns {Record<string, Record<string, boolean>>|null} */
export async function fetchKonekoAccessOverrides() {
  const base = getKonekoBudgetApiBaseUrl();
  const url = joinApi(base, "api/koneko/v1/access/");
  const data = await getJsonOrNull(url);
  if (!data || typeof data !== "object") return null;
  if (data.overrides && typeof data.overrides === "object") {
    return data.overrides;
  }
  return null;
}

/** @returns {Promise<{ ok: boolean, status: number, detail: string }>} */
export async function putKonekoAccessOverrides(
  overrides,
  {replace = false} = {}
) {
  const base = getKonekoBudgetApiBaseUrl();
  const url = joinApi(base, "api/koneko/v1/access/");
  return putJson(url, {overrides: overrides || {}, replace: Boolean(replace)});
}
